/**
 * Timeline options for audit comparison
 * - 'current': Shows current audit data
 * - 'compare': Shows comparison between first and current audit (First vs. Current)
 */
export type AuditTimeline = 'current' | 'compare';

/**
 * PDF generation mode
 * - 'manual': User manually generates PDF
 * - 'automatic': PDF is generated automatically based on frequency
 */
export type GenerationMode = 'automatic' | 'manual';

/**
 * Frequency for automatic PDF generation
 */
export type AutoFrequency = 'week' | 'month';

/**
 * Enum-like mapping for PDF sections
 * This ensures type safety and prevents hardcoding section names
 */
export enum PDFSection {
  AI_SUMMARY = 'ai_summary',
  OVERVIEW = 'site_overview',
  TASKS = 'seo_tasks',
  SCANNED_PAGES = 'scanned_pages',
  AI_VISIBILITY = 'ai_visibility',
  KEYWORD_TRACKING = 'keyword_tracking',
  LOCAL_SEO = 'local_seo'
}

/**
 * Type alias for report sections using the enum values
 */
export type ReportSection = `${PDFSection}`;

/**
 * A single goal option for the AI Summary "Goals of Report" field.
 * Uses { key, name } structure to match API payloads and dropdown options.
 */
export interface ReportGoalOption {
  key: ReportSection;
  name: string;
}

/**
 * AI Summary Format Options
 */
export type SummaryFormat = 'short' | 'bullet_list' | 'detailed';

/**
 * AI Summary Tone of Voice Options
 */
export type ToneOfVoice = 'formal' | 'professional' | 'neutral';

export interface AiSummaryResponse {
  goal: ReportSection;
  name: string;
  response: string;
}

/**
 * AI Summary Configuration
 */
export interface AiSummaryConfig {
  format?: SummaryFormat;
  tone?: ToneOfVoice;
  /** Multi-selection: array of goal objects with { key, name } structure */
  goal?: ReportGoalOption[];
  previewText?: string | Record<string, string> | AiSummaryResponse[];
}

/**
 * Main PDF Configuration Interface
 * This represents the complete state for PDF builder configuration
 */
export interface PDFConfig {
  /** Timeline selection for audit comparison */
  timeline: AuditTimeline;

  /** Generation mode (manual or automatic) */
  generationMode: GenerationMode;

  /** Frequency for automatic generation */
  frequency: AutoFrequency;

  /** Array of section keys that should be included in the PDF */
  includedSections: ReportSection[];

  /** Custom icon URL or identifier */
  customIcon: string | null;

  /** Array of validated email addresses (max 5) */
  recipients: string[];

  /** AI Summary configuration */
  aiSummary: AiSummaryConfig;

  /** When true (and Local SEO section is included), include all keywords in the report */
  includeAllKeywords?: boolean;

  /** Local SEO specific options (resource name and keyword inclusion) */
  localSeoOptions?: {
    name: string | null;
    includeAllKeywords: boolean;
  };

  /** Last saved date filters */
  periodFilter: string;
  compareFilter: string;
}

/**
 * Payload structure for PDF generation/report
 */
export interface PDFPayload {
  siteId: string | undefined;
  /** Full map — every section key with true/false */
  sectionsVisibility?: { [key: string]: boolean };
  /** Only present when a custom icon is actually set */
  customIcon?: string | null;
  timeline: {
    periodFilter: string | null;
    compareFilter: string | null;
    periodStartDate: number;
    periodEndDate: number;
    compareStartDate: number;
    compareEndDate: number;
  };
  /** Only present when Local SEO section is checked AND location data exists */
  localSeoOptions?: {
    /** Location resource name, e.g. "locations/6861452627793949586" */
    name: string;
    includeAllKeywords: boolean;
    selectedKeywords: string[];
  };
  /** Only present when AI Summary section is checked */
  aiSummaryOptions?: {
    // previewText: string;
    goal: ReportGoalOption[] | undefined;
    tone: string | undefined;
    format: SummaryFormat | undefined;
  };
  emailOptions?: {
    recipients: string[];
    fileName: string;
    description: string;
  };
  scheduleOptions?: {
    frequency: string;
    day?: string | number;
  };
}

/**
 * Legacy interface for backward compatibility
 * @deprecated Use PDFConfig instead
 */
export interface PdfConfig extends PDFConfig {
  sections: ReportSection[];
  selectedIcon?: string | null;
}

/**
 * Section visibility map for conditional rendering
 */
export interface SectionVisibilityMap {
  [PDFSection.AI_SUMMARY]: boolean;
  [PDFSection.OVERVIEW]: boolean;
  [PDFSection.TASKS]: boolean;
  [PDFSection.SCANNED_PAGES]: boolean;
  [PDFSection.AI_VISIBILITY]: boolean;
  [PDFSection.KEYWORD_TRACKING]: boolean;
  [PDFSection.LOCAL_SEO]: boolean;
}

/**
 * Helper type for partial PDF config updates
 */
export type PartialPDFConfig = Partial<PDFConfig>;
